//==================================================================================================
// Kitten Cat LLC ("COMPANY") CONFIDENTIAL                                                         =
// Unpublished Copyright (c) 2021 Kitten Cat LLC. All rights reserved.                             =
//                                                                                                 =
// NOTICE: All information contained herein is, and remains the property of COMPANY. The           =
// intellectual and technical concepts contained herein are proprietary to COMPANY and may be      =
// covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret      =
// or copyright law. Dissemination of this information or reproduction of this material is         =
// strictly forbidden unless prior written permission is obtained from COMPANY. Access to the      =
// source code contained herein is hereby forbidden to anyone except current COMPANY               =
// employees, managers, or contractors who have executed Confidentiality and Non-disclosure        =
// agreements explicitly covering such access.                                                     =
//                                                                                                 =
// The copyright notice above does not evidence any actual or intended publication or              =
// disclosure of this source code, which includes information that is confidential and/or          =
// proprietary, and is a trade secret, of COMPANY. ANY REPRODUCTION, MODIFICATION,                 =
// DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE CODE       =
// WITHOUT THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF      =
// APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE       =
// AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR        =
// DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE, IN      =
// WHOLE OR IN PART.                                                                               =
//==================================================================================================

import 'instant.page';
import * as Y from 'yjs';
import { WebrtcProvider } from 'y-webrtc';
import { MonacoBinding } from 'y-monaco';

// TODO upper import is smaller but breaks icons for auto-complete
// import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import * as monaco from 'monaco-editor';

import Canvg from 'canvg';

import 'bootstrap/js/dist/button';
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/dropdown';
import 'bootstrap/dist/css/bootstrap.min.css';
import './editor-common.css';

import importPromiseUrlEncoder from './url_encoder';
import importPromiseParser from './parser';

const exampleAzure01 = `
# Source: https://docs.microsoft.com/en-us/azure/architecture/solution-ideas/media/advanced-analytics-on-big-data.png
# Source: https://docs.microsoft.com/en-us/azure/architecture/icons/

ILogs:          icon name "azure_10801-icon-service-Files" "Logs, files and" "media (unstructured)"
IBrowser:       icon name "azure_10783-icon-service-Browser" "Business/custom" "apps (structured)" \\
                with .n at ILogs.s + (0, 200)
IDataFactory:   icon name "azure_10126-icon-service-Data-Factory" "Azure" "Data Factory" \\
                with .c at (ILogs.e.x + 200, (ILogs.c.y + IBrowser.c.y) / 2)
IStorage:       icon name "azure_10090-icon-service-Data-Lake-Storage-Gen1" "Azure Data" "Lake Storage" \\
                with .c at IDataFactory.c + (150, 0)
IDataBricks:    icon name "azure_10787-icon-service-Azure-Databricks" "Azure" "Databricks" \\
                with .c at IStorage.c + (150, 0)
ICosmosDB:      icon name "azure_10121-icon-service-Azure-Cosmos-DB" "Azure" "Cosmos DB" \\
                with .c at IDataBricks.c + (150, 150)
IWebApp:        icon name "azure_10803-icon-service-Folder-Website" "Web" "Application" \\
                with .c at ICosmosDB.c + (150, 0)
IPowerBI:       icon name "azure_10148-icon-service-Analysis-Services" "Power BI" \\
                with .c at IWebApp - (0, 150)
ISynAna:        icon name "azure_00606-icon-service-Azure-Synapse-Analytics" "Azure Synapse" "Analytics" \\
                with .c at IDataBricks - (-150, 150)
IAnalysis:      icon name "azure_10148-icon-service-Analysis-Services" "Azure Analysis" "Services" \\
                with .c at ISynAna + (150, 0)

A0a: arrow from ILogs.e + (20, 0) right 100 then down (IDataFactory.w.y - ILogs.e.y) to IDataFactory.w - (10, 0) \\
     stroke darkgray
A0b: arrow from IBrowser.e + (20, 0) right 100 then up (IBrowser.e.y - IDataFactory.w.y) to IDataFactory.w - (10, 0) \\
     stroke darkgray
A1: arrow "(1)" from IDataFactory.e + (20, 0) to IStorage.w - (10, 0) stroke darkgray
A2: arrow "(2)" from IStorage.e + (20, 0) to IDataBricks.w - (10, 0) stroke darkgray
text "(3)" with .nw at IDataBricks.nw - (20, 10)
A4: arrow <- from IDataBricks.e + (35, -20) right (ICosmosDB.n.y - IDataBricks.e.y - 35) \
    up to ISynAna.s + (0, 40) stroke darkgrey
text "(4)" with .c at (A4.end.x, A4.end.y + 30)

A5: arrow "(5)" from IDataBricks.e + (20, 0) to IPowerBI.w - (20, 0) stroke darkgray
A6: arrow "(6)" from IAnalysis.s + (0, 30) down to IPowerBI.n stroke darkgray
A7: arrow <-> from A4.start + (0, 40) right (ICosmosDB.n.y - IDataBricks.e.y - 35) \
    down to ICosmosDB.n - (0, 10) stroke darkgrey
text "(7)" with .c at (A7.end.x, A7.end.y - 30)

Ingest: box \\
    from IDataFactory.nw - (30, 10) \\
    to IDataFactory.se + (30, 40) \\
    fill grey 0.1 stroke none
text "Ingest" with .nw at Ingest.nw - (0, 25)

Store: box \\
    with .nw at IStorage.nw - (30, 10) \\
    width Ingest.width \\
    height Ingest.height \\
    fill grey 0.1 stroke none
text "Store" with .nw at Store.nw - (0, 20)

PrepAndTrain: box \\
    with .nw at IDataBricks.nw - (30, 10) \\
    width Ingest.width \\
    height Ingest.height \\
    fill grey 0.1 stroke none
text "Prep and train" with .nw at PrepAndTrain.nw - (0, 25)

CosmosDBBackground: box \\
    with .nw at ICosmosDB.nw - (30, 10) \\
    width Ingest.width \\
    height Ingest.height \\
    fill grey 0.1 stroke none

PowerBIBackground: box \\
    with .nw at IPowerBI.nw - (30, 10) \\
    width Ingest.width \\
    height Ingest.height \\
    fill grey 0.1 stroke none

ModelAndServeBackground: box \\
    from ISynAna.nw - (40, 10) \\
    to IAnalysis.se + (50, 50)\\
    width Ingest.width \\
    height Ingest.height \\
    fill grey 0.1 stroke none
text "Model and serve" with .nw at ModelAndServeBackground.nw - (0, 20)
`;

function triggerDownload(imgURI, filename) {
    const evt = new MouseEvent('click', {
        view: window,
        bubbles: false,
        cancelable: true,
    });

    const a = document.createElement('a');
    a.setAttribute('download', filename);
    a.setAttribute('href', imgURI);
    a.setAttribute('target', '_blank');

    a.dispatchEvent(evt);
}

function setupSaveAsPngButton() {
    const element = document.getElementById('save_as_png_button');
    element.onclick = async () => {
        /* eslint-disable */
        const canvas = document.getElementById('canvas');
        const rendered = document.getElementById('rendered');
        const ctx = canvas.getContext('2d');
        const data = rendered.innerHTML;
        const renderer = Canvg.fromString(ctx, data);
        await renderer.render();
        const encodedURL = canvas.toDataURL('image/png');
        triggerDownload(encodedURL, 'image.png');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    return element;
}

function setupSaveAsSvgButton() {
    const element = document.getElementById('save_as_svg_button');
    element.onclick = async function () {
        const rendered = document.getElementById('rendered');
        const data = rendered.innerHTML;
        const svgBlob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
        const DOMURL = window.URL || window.webkitURL || window;
        const url = DOMURL.createObjectURL(svgBlob);
        triggerDownload(url, 'image.svg');
    };
    return element;
}

function setupVersion(parser) {
    const element = document.getElementById('version');
    element.innerHTML = `Running version: ${parser.get_git_sha()}`;
    return element;
}

function setupMonacoEditorComponent(monacoEditorAutomaticLayout: boolean, autoCompleteSuggestionsWrapper: AutoCompleteSuggestionsWrapper) {
    const element = document.getElementById('code');

    function createDependencyProposals(range) {
        // returning a static list of proposals, not even looking at the prefix (filtering is done by the Monaco editor),
        // here you could do a server side lookup
        return autoCompleteSuggestionsWrapper.autoCompleteSuggestions.map((elem) => ({
            label: elem.value,
            insertText: elem.value,
            kind: monaco.languages.CompletionItemKind.Text,
            range,
        }));
    }

    monaco.languages.register({ id: 'sheeptext' });
    monaco.languages.registerCompletionItemProvider('sheeptext', {
        provideCompletionItems(model, position) {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };
            return {
                suggestions: createDependencyProposals(range),
            };
        },
    });

    const editor = monaco.editor.create(element, {
        value: '',
        language: 'sheeptext',
        minimap: {
            enabled: false,
        },
        scrollbar: {
            vertical: 'visible',
        },
        automaticLayout: monacoEditorAutomaticLayout,
    });

    return editor;
}

function connect_yjs(editor) {
    /* eslint-disable */
    const ydoc = new Y.Doc();
    const provider = new WebrtcProvider(
        '95D7BF1B11124A9A96F887147BEE90B3',
        ydoc,
        {
            password: 'DA4976D159594432A355B84626242CCC',
            signaling: [
                'wss://signaling.yjs.dev',
                'wss://y-webrtc-signaling-eu.herokuapp.com',
                'wss://y-webrtc-signaling-us.herokuapp.com',
                // 'ws://localhost:4444',
            ],
            filterBcConns: false,
        },
    );
    const type = ydoc.getText('monaco');
    const monacoBinding = new MonacoBinding(
        type,
        editor.getModel(),
        new Set([editor]),
        provider.awareness,
    );
    return provider;
}

function is_fragment_present() {
    const fragment_on_load = window.location.hash.split('#')[1];
    return fragment_on_load !== undefined;
}

function restore_fragment(editor, url_encoder) {
    const fragment_on_load = window.location.hash.split('#')[1];
    if (fragment_on_load === undefined) {
        return;
    }
    const data = JSON.parse(url_encoder.url_decode(fragment_on_load));
    editor.setValue(data.text);
    if (data.svg !== '') {
        const rendered = document.getElementById('rendered');
        rendered.innerHTML = data.svg;
    }
}

function render(editor: monaco.editor.IStandaloneCodeEditor, autoCompleteSuggestionsWrapper: AutoCompleteSuggestionsWrapper, parser, url_encoder, update_url_fragment) {
    const rendered = document.getElementById('rendered');
    const contents: string = editor.getValue();
    try {
        const result = parser.text_diagram_parse(contents);
        if (result.autoCompleteSuggestions !== undefined) {
            autoCompleteSuggestionsWrapper.autoCompleteSuggestions = result.autoCompleteSuggestions;
        }
        if (result.is_successful) {
            rendered.innerHTML = result.svg;
            if (update_url_fragment) {
                const data = {
                    text: contents,
                    // "svg": result.svg,
                    svg: '',
                };
                window.location.hash = url_encoder.url_encode(JSON.stringify(data));
            }
        } else {
            rendered.innerHTML = `<pre>${result.error_message}</pre>`;
        }
    } catch (error) {
        rendered.innerHTML = `<pre>${error}</pre>`;
    }
}

function setupExamples(editor) {
    document.getElementById('load_example_azure_01').onclick = function () {
        editor.setValue(exampleAzure01);
    };
}

function setupClearButton(editor, update_url_fragment) {
    const element = document.getElementById('clear');
    element.onclick = function () {
        editor.setValue('');
        if (update_url_fragment) {
            window.location.hash = '';
        }
    };
}

function setLoadingFinished() {
    document.getElementById('loading-section').className = 'd-none';
    document.getElementById('loaded-section').className = 'd-block';
}

export interface AutoCompleteSuggestion {
    value: string,
    score: BigInt,
    meta: string
}

export class AutoCompleteSuggestionsWrapper {
    autoCompleteSuggestions: AutoCompleteSuggestion[];

    constructor() {
        this.autoCompleteSuggestions = [];
    }
}

export class EditorContext {
    readonly monacoEditor: monaco.editor.IStandaloneCodeEditor;
    autoCompleteSuggestionsWrapper: AutoCompleteSuggestionsWrapper;

    constructor(monacoEditor: monaco.editor.IStandaloneCodeEditor, autoCompleteSuggestionsWrapper: AutoCompleteSuggestionsWrapper) {
        this.monacoEditor = monacoEditor;
        this.autoCompleteSuggestionsWrapper = autoCompleteSuggestionsWrapper;
    }
}

export class EditorCommonOptions {
    readonly updateUrlFragment: boolean;
    readonly tutorialMode: boolean;
    readonly monacoEditorAutomaticLayout: boolean;

    constructor(
        {
            updateUrlFragment,
            tutorialMode,
            monacoEditorAutomaticLayout,
        }:
            {
                updateUrlFragment: boolean,
                tutorialMode: boolean,
                monacoEditorAutomaticLayout: boolean,
            },
    ) {
        this.updateUrlFragment = updateUrlFragment;
        this.tutorialMode = tutorialMode;
        this.monacoEditorAutomaticLayout = monacoEditorAutomaticLayout;
    }
}

export async function loadEditorCommon(options: EditorCommonOptions): Promise<EditorContext> {
    const autoCompleteSuggestionsWrapper = new AutoCompleteSuggestionsWrapper();
    const editor = setupMonacoEditorComponent(options.monacoEditorAutomaticLayout, autoCompleteSuggestionsWrapper);
    editor.layout();
    if (options.updateUrlFragment && is_fragment_present()) {
        await importPromiseUrlEncoder().then((url_encoder) => {
            restore_fragment(editor, url_encoder);
        });
    } else {
        // TODO re-enable later
        // const provider = connect_yjs(editor);
    }

    const [url_encoder, parser] = await Promise.all([importPromiseUrlEncoder(), importPromiseParser()]);
    editor.onDidChangeModelContent((e) => {
        render(editor, autoCompleteSuggestionsWrapper, parser, url_encoder, options.updateUrlFragment);
    });

    if (!options.tutorialMode) {
        setupSaveAsPngButton();
        setupSaveAsSvgButton();
        setupVersion(parser);
        setupExamples(editor);
        setupClearButton(editor, options.updateUrlFragment);
    }

    if (editor.getValue() !== '') {
        render(editor, autoCompleteSuggestionsWrapper, parser, url_encoder, options.updateUrlFragment);
    }

    setLoadingFinished();

    return new EditorContext(editor, autoCompleteSuggestionsWrapper);
}
